import React, { lazy, useEffect, useState } from 'react';
import cx from 'classnames';

import { Layout } from '@atlas/components';
import {
  AccountManager,
  Bus,
  Fuse,
  PreferencesProvider,
  sessionId,
  SiteConfigManager,
  StorageManager,
  useApplicationState,
  useSettings,
  useTranslation,
} from '@atlas/core';
import { staticRoutes } from '@atlas/routing';
import {
  Button,
  HEADER_LAYOUT_TYPES,
  HeaderMobileLayout,
  ScrollableViewportObserver,
} from '@atlas/ui';

import Logo from 'common/Widgets/Logo/Logo';
import CustomStylesPortal from 'components/CustomStylesPortal/CustomStylesPortal';
import HeaderSkeleton from 'components/Header/HeaderSkeleton';
import { LangSwitchWithoutRouter } from 'components/LangSwitch/LangSwitch';
import useThemeBootstrap from 'components/Layout/useThemeBootstrap';
import updateMeta from 'components/SeoPage/updateMeta';
import { isRtlLanguage } from 'helpers/isRtlLanguage';

import { ACCOUNT_STATUSES, CXFROM_STORAGE_KEY } from 'common/constants';
import { usePostHog } from 'hooks/usePostHog';
import NotificationStatePopup from 'common/Popup/NotificationStatePopup';
import { TerminalLoginPopupManager } from 'common/Popup/TerminalLoginPopup/TerminalLoginPopupManager';
import Copyright from 'components/Footer/Copyright';
import DebugInfo from 'src/components/DebugInfo/DebugInfo';
import LayoutErrorFallback from './LayoutErrorFallback';

const SingleSignUp = lazy(
  () => import(/* webpackChunkName: "SingleSignUp" */ 'components/Auth/SingleSignUp'),
);

const AuthorizationLoader = ({ children }) => {
  const { resolved } = useApplicationState();

  if (resolved) {
    return children;
  }

  return null;
};

const Header = () => {
  const userData = useApplicationState();
  const translations = useTranslation();
  const translationsLoaded = Object.keys(translations).length > 0;

  const layout =
    SiteConfigManager.getConfig('config.config.general.mobileHeaderView') ||
    HEADER_LAYOUT_TYPES.HEADER1;

  const headerContentClassName = cx('header-content-block', {
    'with-background': layout === HEADER_LAYOUT_TYPES.HEADER1,
  });

  if (translationsLoaded && userData.resolved) {
    return (
      <HeaderMobileLayout className="header" type={layout}>
        <HeaderMobileLayout.Logo>
          <Logo linkTo="/" />
        </HeaderMobileLayout.Logo>
        <HeaderMobileLayout.Content>
          <div className={headerContentClassName}>
            <LangSwitchWithoutRouter isSmall />
            <div className="header-button-block">
              <a href={staticRoutes.signin}>
                <Button block label="Sign in" size="medium" type="primary" />
              </a>
            </div>
          </div>
        </HeaderMobileLayout.Content>
      </HeaderMobileLayout>
    );
  }

  return <HeaderSkeleton />;
};

const SignUpRedirect = () => {
  const [linkToRedirect, setLinkToRedirect] = useState(null);

  useEffect(() => {
    Bus.on(Bus.events.auth.simpleLogin, ({ redirectLink, sessionToken } = {}) => {
      sessionToken && AccountManager.setToken(sessionToken);
      redirectLink && setLinkToRedirect(redirectLink);
      if ((sessionToken && linkToRedirect) || (redirectLink && AccountManager.getToken())) {
        window.location = redirectLink || linkToRedirect;
      }
    });
  }, [linkToRedirect]);

  return null;
};

const SignUpLayout = ({ optimized = false }) => {
  useThemeBootstrap();

  const { lang } = useSettings();
  const { authenticated, connection, resolved, account } = useApplicationState();
  const params = new URLSearchParams(window.location.search);
  const cxfrom = params.get('cxfrom');

  const { init, login } = usePostHog();
  useEffect(() => {
    init().then(() => {
      login(sessionId);
    });
  }, [init, login]);

  useEffect(() => {
    updateMeta();
  }, []);

  useEffect(() => {
    document.body.className = cx({ rtl: isRtlLanguage({ lang }) });
  }, [lang]);

  useEffect(() => {
    if (resolved && authenticated && account.status !== ACCOUNT_STATUSES.PARTIALLY_REGISTERED) {
      window.location.href = cxfrom || '/';
    }
  }, [authenticated, cxfrom, resolved, account.status]);

  const isShowRegForm = SiteConfigManager.getConfig('config.config.enable_registration');

  const layoutClassName = cx('layout', {
    disconnected: !connection.connected,
    'signup-layout': isShowRegForm,
  });

  useEffect(() => {
    if (!cxfrom) return;

    StorageManager.setItem(CXFROM_STORAGE_KEY, cxfrom);
  }, [cxfrom]);

  const wrapperClassName = cx('layout-wrapper');

  return (
    <Fuse fallback={LayoutErrorFallback}>
      <CustomStylesPortal />
      <TerminalLoginPopupManager />
      <AuthorizationLoader>
        <Layout>
          <Layout.Header sticky>
            <Header />
          </Layout.Header>
          <Layout.Content>
            <PreferencesProvider>
              <ScrollableViewportObserver className={wrapperClassName} id="content">
                <div className={layoutClassName}>
                  {isShowRegForm && (
                    <>
                      <SignUpRedirect />
                      <SingleSignUp optimized={optimized} />
                      <Copyright />
                    </>
                  )}
                  {!isShowRegForm && (
                    <NotificationStatePopup
                      className="not-found-page"
                      message="The page you are looking for may have been deleted"
                      title="Page not found"
                    >
                      <div className="sticky-bottom-buttons-container double without-padding-bg">
                        <a className="primary" href={staticRoutes.sportsbook}>
                          <Button label="GO To sport" size="big" type="primary" />
                        </a>
                        <a className="primary" href={staticRoutes.casino}>
                          <Button label="Go to Casino" size="big" type="primary" />
                        </a>
                      </div>
                    </NotificationStatePopup>
                  )}
                  <DebugInfo />
                </div>
              </ScrollableViewportObserver>
            </PreferencesProvider>
          </Layout.Content>
        </Layout>
      </AuthorizationLoader>
    </Fuse>
  );
};

export default SignUpLayout;
